import {useCallback, useEffect, useState} from 'react';
import type {EmblaCarouselType} from 'embla-carousel';

import {usePrefersReducedMotion} from '@/modules/foundation/hooks/usePrefersReducedMotion';

type UseDotButtonType = {
	selectedIndex: number;
	scrollSnaps: number[];
	onDotButtonClick: (index: number) => void;
};

export const useDotButton = (emblaApi: EmblaCarouselType | undefined): UseDotButtonType => {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

	const onDotButtonClick = useCallback(
		(index: number) => {
			if (!emblaApi) {
				return;
			}
			emblaApi.scrollTo(index, prefersReducedMotion);
		},
		[emblaApi, prefersReducedMotion],
	);

	const onInit = useCallback((emblaApi: EmblaCarouselType) => {
		setScrollSnaps(emblaApi.scrollSnapList());
	}, []);

	const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, []);

	useEffect(() => {
		if (!emblaApi) {
			return;
		}

		// NOTE: Using the setTimeout here feels wrong, but since we're not allowed to call
		//       setState from a useEffect anymore (maybe we never were?) we have to delay the
		//       initialization until after the useEffect has run. Ideally we would use the
		//       "init" event from embla, but that may already have fired before the emblaApi is
		//       available here.
		const timeout = setTimeout(() => onInit(emblaApi), 0);
		const handler = emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
		return () => {
			clearTimeout(timeout);
			handler.clear();
		};
	}, [emblaApi, onInit, onSelect]);

	return {
		selectedIndex,
		scrollSnaps,
		onDotButtonClick,
	};
};
